import React, { useEffect, useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import EmailRecipientFields, { emptyRoutingState, routingApiPayload } from './EmailRecipientFields';

/**
 * Pflicht-Voransicht vor jedem Operator-E-Mail-Versand.
 * Empfängeradresse ist immer sichtbar; extra An / CC / BCC können ergänzt werden.
 * Bestätigen („Jetzt senden“) ist der einzige Weg zum eigentlichen Versandaufruf.
 */
export default function EmailPreviewConfirm({
  open,
  onClose,
  onConfirm,
  confirming = false,
  title = 'E-Mail-Voransicht',
  description = 'Bitte Inhalt und Empfänger prüfen. Versand erst nach Bestätigung.',
  to,
  toLabel,
  recipients,
  subject,
  html,
  attachmentNote,
  sampleNote,
  confirmLabel = 'Jetzt senden',
  testId = 'email-preview-confirm',
}) {
  const [routing, setRouting] = useState(emptyRoutingState());

  useEffect(() => {
    if (open) setRouting(emptyRoutingState());
  }, [open, to, subject]);

  const list = Array.isArray(recipients) ? recipients.filter((r) => r && (r.email || r)) : [];
  const hasPrimary = !!(to || toLabel || list.length);
  const canSend = !!html && hasPrimary && !confirming;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !confirming) onClose(); }}>
      <DialogContent
        className="max-w-3xl h-[85vh] flex flex-col"
        data-testid={testId}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 text-sm shrink-0">
          <div data-testid={`${testId}-to`}>
            <span className="text-slate-500">An:</span>{' '}
            {list.length > 1 ? (
              <span className="font-medium text-slate-800">{list.length} Empfänger</span>
            ) : hasPrimary ? (
              <>
                <span className="font-medium text-slate-800">{toLabel || to || list[0]?.name || list[0]?.email}</span>
                {to && toLabel ? <span className="text-slate-500"> · {to}</span> : null}
                {!to && !toLabel && list[0]?.email && list[0]?.name ? (
                  <span className="text-slate-500"> · {list[0].email}</span>
                ) : null}
              </>
            ) : (
              <span className="font-medium text-amber-800">Keine E-Mail-Adresse hinterlegt — Versand nicht möglich</span>
            )}
          </div>
          {list.length > 1 && (
            <ul
              className="max-h-28 overflow-y-auto rounded border border-slate-200 divide-y divide-slate-100 text-xs"
              data-testid={`${testId}-recipients`}
            >
              {list.map((r, i) => {
                const email = typeof r === 'string' ? r : r.email;
                const name = typeof r === 'string' ? null : (r.name || r.label);
                return (
                  <li key={`${email}-${i}`} className="px-2 py-1 flex justify-between gap-2">
                    <span className="text-slate-700 truncate">{name || email || '—'}</span>
                    {name ? <span className="text-slate-600 font-mono shrink-0">{email}</span> : null}
                  </li>
                );
              })}
            </ul>
          )}
          {subject != null && subject !== '' && (
            <div data-testid={`${testId}-subject`}>
              <span className="text-slate-500">Betreff:</span>{' '}
              <span className="font-medium text-slate-800">{subject}</span>
            </div>
          )}
          {attachmentNote && (
            <div className="text-xs text-slate-500" data-testid={`${testId}-attachment`}>
              {attachmentNote}
            </div>
          )}
          {sampleNote && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                 data-testid={`${testId}-sample-note`}>
              {sampleNote}
            </div>
          )}
          <EmailRecipientFields
            extraTo={routing.extraTo}
            cc={routing.cc}
            bcc={routing.bcc}
            onChange={setRouting}
            disabled={confirming}
            testId={`${testId}-routing`}
          />
        </div>

        <div className="flex-1 min-h-0 rounded border border-slate-200 bg-white overflow-hidden">
          {html ? (
            <iframe
              title="E-Mail-Voransicht"
              sandbox=""
              srcDoc={html}
              className="w-full h-full border-0"
              data-testid={`${testId}-iframe`}
            />
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-slate-400">
              Kein HTML-Inhalt
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0 shrink-0">
          <Button variant="outline" onClick={onClose} disabled={confirming} data-testid={`${testId}-cancel`}>
            Abbrechen
          </Button>
          <Button
            onClick={() => onConfirm(routingApiPayload(routing))}
            disabled={!canSend}
            data-testid={`${testId}-send`}
          >
            {confirming
              ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              : <Send className="h-4 w-4 mr-1.5" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
